import { useEffect, useState } from "react";
import clsx from "clsx";
import { HttpStatusCode } from "axios";

import {
  RentalStockHistoryItem,
  RentalStockHistorySummary,
  RentalStockHistoryType,
  RentalStockItem,
  getCountStringFromRentalStockHistoryType,
  getRentalStockDetail,
  getRentalStockHistory,
  getRentalStockTitleList,
  getStringFromRentalStockHistorySummary,
  getStringFromRentalStockHistoryType,
} from "../../../../../../services/rentalStockServices";

import useFullScreenLoading from "../../../../../../hooks/useFullScreenLoading";
import useInfiniteScroll, {
  INFINITE_CODE,
  InfiniteScrollCallbackArgs,
} from "../../../../../../hooks/useInfiniteScroll";
import useVirtualNumber from "../../../../../../hooks/useVirtualNumber";

import CommonButton from "../../../../../../components/Button";
import { RentalStockSummaryList } from "../../../../../../components/RentalStock/RentalStockSummaryList";
import HorizontalRule from "../../../../../../components/Rule/Horizontal";
import RentalStockChangePopup from "../../../../../../components/RentalStock/RentalStockChangePopup";
import CommonSelect from "../../../../../../components/Select";
import CommonPopup from "../../../../../../components/Popup";
import BottomGroup from "../../../../../../components/Button/Group";

import { PostListNoPostIcon } from "../../../../../../components/Talk/PostList";

type Props = {
  storeId?: string;
  active: boolean;
};

export default function RentalStockHistory({
  storeId,
  active,
}: Props): [{ RentalStockHistoryReload: () => void }, React.ReactNode] {
  const [popup, setPopup] = useState<string>("");
  const [
    activeRentalStockHistorySummaryButton,
    setActiveRentalStockHistorySummaryButton,
  ] = useState<string>("totalCount");

  const [isLoading, setIsLoading] = useState<boolean>(active);
  const loadingComponent = useFullScreenLoading({ isLoading });

  const [virtualNumberHandler, virtualNumberComponent] = useVirtualNumber({});

  const [rentalStock, setRentalStock] = useState<RentalStockItem | undefined>(
    undefined
  );

  const [rentalStockList, setRentalStockList] = useState<RentalStockItem[]>([]);

  const [rentalStorkHistorySummary, setRentalStockHistorySummary] = useState<
    RentalStockHistorySummary | undefined
  >(undefined);

  const [rentalStockHistory, setRentalStockHistory] = useState<
    RentalStockHistoryItem[]
  >([]);

  const [historyType, setHistoryType] = useState<RentalStockHistoryType | null>(
    null
  );

  const [telNumber, setTelNumber] = useState<string>("");

  const loadRentalStockList = async () => {
    try {
      const result = await getRentalStockTitleList({
        sort: "createdAt.desc",
        storeId: storeId,
      });

      if (result.status === HttpStatusCode.Ok) {
        setRentalStockList(result.data.items);

        const target = result.data.items.find(
          (item) => item.stockId === rentalStock?.stockId
        );

        if (rentalStock === undefined || target === undefined) {
          setRentalStock(result.data.items[0]);
        } else {
          setRentalStock(target);
        }
      }
    } catch (e: any) {
      setRentalStockList([]);
      setRentalStockHistorySummary(undefined);
      setRentalStock(undefined);
      console.log(e);
    }
  };

  const loadRentalStockHistoryList = async (
    args?: InfiniteScrollCallbackArgs
  ) => {
    // console.log("loadRentalStockHistoryList", args);
    let itemLength = INFINITE_CODE.ERROR;

    if (!rentalStock) {
      return itemLength;
    }

    setIsLoading(true);
    try {
      const result = await getRentalStockHistory({
        historyType,
        stockId: rentalStock.stockId,
        ...args,
      });

      if (result.status === HttpStatusCode.Ok) {
        itemLength = result.data.list.items.length;
        setRentalStockHistorySummary(result.data.summary);

        if (args?.page === 1) setRentalStockHistory(result.data.list.items);
        else
          setRentalStockHistory((prev) => [
            ...(prev ?? []),
            ...result.data.list.items,
          ]);
      } else {
        itemLength = INFINITE_CODE.ERROR;
        setRentalStockHistorySummary(undefined);
        setRentalStockHistory([]);
      }
    } catch (e: any) {
      setRentalStockHistorySummary(undefined);
      setRentalStockHistory([]);
      console.log(e);
    } finally {
      setIsLoading(false);
    }
    return itemLength;
  };

  const loadRentalStockDetail = async (stockId: string) => {
    try {
      const result = await getRentalStockDetail(stockId);
      if (result.status === HttpStatusCode.Ok) {
        setRentalStock(result.data);
      }
    } catch (e: any) {
      console.log(e);
    }
  };

  const PAGE_SIZE = 20; //20으로 수정 필요
  const SCROLL_LOAD_START_PAGE = 2;
  const [, { resetScroll }] = useInfiniteScroll({
    isLoading,
    callback: loadRentalStockHistoryList,
    startPage: SCROLL_LOAD_START_PAGE,
    pageSize: PAGE_SIZE,
  });

  const handleRentalStockHistorySummaryButtonClick = (target: string) => {
    setActiveRentalStockHistorySummaryButton(target);

    if (target === "totalCount") setHistoryType(null);
    else if (target === "totalModifiedCount") setHistoryType("TOTAL_MODIFIED");
    else if (target === "lostDamagedCount") setHistoryType("LOST_DAMAGED");
    else if (target === "restoredCount") setHistoryType("RESTORED");
  };

  const reloadAll = (reset = true) => {
    resetScroll();
    setRentalStockList([]);
    setRentalStockHistory([]);
    if (storeId === undefined) {
      setRentalStockHistorySummary(undefined);
      setRentalStock(undefined);
    }
    if (reset) {
      if (rentalStockList.length > 0) {
        setRentalStock(rentalStockList[0]);
      } else {
        setRentalStockHistorySummary(undefined);
      }
    }
    setActiveRentalStockHistorySummaryButton("totalCount");

    loadRentalStockList();
  };

  const telHandler = (telNumber: string) => {
    setTelNumber(telNumber);
    setPopup("tel");
  };

  useEffect(() => {
    setRentalStock(undefined);
    reloadAll();
  }, [storeId]);

  useEffect(() => {
    if (rentalStock) {
      resetScroll();
      loadRentalStockHistoryList({
        page: SCROLL_LOAD_START_PAGE - 1,
        pageSize: PAGE_SIZE,
      });

      if (!rentalStock.stockSummary) {
        loadRentalStockDetail(rentalStock.stockId);
      }
    }
  }, [rentalStock, activeRentalStockHistorySummaryButton]);

  useEffect(() => {
    if (active && rentalStockList.length > 0) {
      setRentalStock(rentalStockList[0]);
      reloadAll();
    }
  }, [active]);

  return [
    { RentalStockHistoryReload: reloadAll },
    <>
      {loadingComponent}
      {rentalStockList && rentalStockList.length > 0 && (
        <>
          <div className="px-5 pt-5">
            <div className="flex flex-row justify-between items-center h-[40px]">
              <CommonSelect
                value={rentalStock?.stockId}
                useTruncate
                arrowIcon={<BlackArrowIcon />}
                optionList={
                  rentalStockList.map((item) => ({
                    value: item.stockId,
                    label: item.goodsName,
                  })) ?? []
                }
                onChange={(target) => {
                  const selectedRentalStock = rentalStockList?.find(
                    (item) => item.stockId === target.value
                  );
                  setRentalStock(selectedRentalStock);
                }}
                className="text-sm font-semibold leading-[19px]"
                labelClassName="design_min:max-w-[calc(100vw-200px)]"
                wrapperClassName="w-fit min-w-[140px] max-w-[160px]"
                variant="storeMenu"
                optionOrder="between"
              />

              <CommonButton
                variant="tertiary"
                className="h-[34px] text-sm font-medium text-brand-neutral-60 whitespace-nowrap"
                onClick={() => {
                  setPopup("rentalStockChange");
                }}
                disabled={rentalStock === undefined}
              >
                재고 변경
              </CommonButton>
            </div>

            {rentalStock ? (
              <RentalStockSummaryList summary={rentalStock.stockSummary} />
            ) : (
              <div className="py-5"></div>
            )}
          </div>
          {rentalStorkHistorySummary && (
            <div className="flex flex-col bg-[#f7f7f7] items-center overscroll-none">
              <div className="px-5 py-2.5 w-full">
                <div className="flex h-[64px] gap-[5px]">
                  {Object.keys(rentalStorkHistorySummary).map((key, idx) => (
                    <RentalStockHistorySummaryButton
                      key={key}
                      active={activeRentalStockHistorySummaryButton === key}
                      disabled={rentalStock === undefined}
                      // count={
                      //   rentalStorkHistorySummary[
                      //     key as keyof RentalStockHistorySummary
                      //   ] ?? 0
                      // }
                      label={getStringFromRentalStockHistorySummary(key)}
                      onClick={() =>
                        handleRentalStockHistorySummaryButtonClick(key)
                      }
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div className="px-5 pt-5 w-max-screen">
        <HorizontalRule margin="0" className="bg-black" />
        <div className="py-[5px] px-2.5 flex justify-between items-center text-xs text-brand-neutral-60 font-normal leading-[18px]">
          <div className="w-[120px]">
            변경일자 /<br />
            변경이력
          </div>
          <div className="w-[90px]">
            대여자
            <br />
            변경자
          </div>
          <div className="w-[40px]">
            전화
            <br />
            번호
          </div>
          <div className="w-[40px]">
            변경
            <br />
            수량
          </div>
          <div className="w-[40px]">
            총<br />
            재고
          </div>
        </div>
        <HorizontalRule margin="0" className="bg-black" />
        {rentalStockHistory && rentalStockHistory.length > 0 ? (
          <div>
            {rentalStockHistory.map((item, index) => (
              <RentalStockHistoryListItem
                key={index}
                item={item}
                telCallback={telHandler}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center w-full h-40 gap-3 text-brand-neutral-60">
            <PostListNoPostIcon />
            재고 변경 이력이 없어요
          </div>
        )}
      </div>

      <CommonPopup
        isOpen={"tel" === popup}
        onClose={() => setPopup("")}
        padding={0}
        unUseHorizonRule
      >
        <div className="min-h-[110px] p-5 text-base font-medium text-center whitespace-pre-line py-10">
          "<span className="text-brand-primary-magenta">{telNumber}</span>
          " 의
          <br />
          번호로 전화를 하시겠습니까?
        </div>
        <div className="pb-[50px]"></div>
        <BottomGroup zIndex="high">
          <div className="flex flex-row h-bottom-button">
            <CommonButton
              variant="grey"
              className="w-full rounded-none"
              onClick={() => {
                setPopup("");
              }}
            >
              취소
            </CommonButton>
            <CommonButton
              className="w-full rounded-none"
              variant="primary"
              onClick={() => {
                window.location.href = `tel:${telNumber}`;
                setPopup("");
              }}
            >
              확인
            </CommonButton>
          </div>
        </BottomGroup>
      </CommonPopup>

      <RentalStockChangePopup
        popup={popup}
        item={rentalStock}
        onClose={() => {
          reloadAll(false);
          setPopup("");
        }}
      />
    </>,
  ];
}

export type RentalStockHistoryListItemProps = {
  item: RentalStockHistoryItem;
  telCallback?: (telNumber: string) => void;
};

const RentalStockHistoryListItem = ({
  item,
  telCallback,
}: RentalStockHistoryListItemProps) => {
  return (
    <div className="p-2.5 flex justify-between items-center font-normal">
      <div className="w-[120px]">
        <div className="leading-4 text-xs">{item.createdAt}</div>

        <div className="leading-[18px] text-sm">
          {getStringFromRentalStockHistoryType(item.historyType)}
        </div>
      </div>
      <div className="min-w-[90px] leading-[18px] text-xs">
        <div className="truncate pr-5">{item.leasedByName || "-"}</div>
        <div className="truncate pr-5">{item.createdByName || "-"}</div>
      </div>
      <div className="w-[40px] flex items-center">
        {item.leasedByMobileNumber ? (
          <button onClick={() => telCallback?.(item.leasedByMobileNumber)}>
            <PhoneIcon />
          </button>
        ) : (
          "-"
        )}
      </div>
      <div
        className={clsx([
          "w-[40px] leading-[18px] text-sm font-medium",
          {
            "text-brand-primary-red": item.historyType === "LOST_DAMAGED",
          },
        ])}
      >
        {getCountStringFromRentalStockHistoryType(item)}
      </div>
      <div className="w-[40px] leading-[18px] text-sm font-medium">
        {item.stockCount ?? 0 | 0}
      </div>
    </div>
  );
};

export type RentalStockHistorySummaryButtonProps = {
  active: boolean;
  count?: number | string;
  label?: string;
  disabled?: boolean;
  onClick?: () => void;
};

export const RentalStockHistorySummaryButton = ({
  active,
  count,
  label,
  onClick,
  disabled = false,
}: RentalStockHistorySummaryButtonProps) => {
  return (
    <div
      className={clsx([
        "flex-1 flex flex-col items-center justify-center rounded-[10px] border p-2.5 bg-white gap-0.5",
        { " text-black border-black": active },
        { "text-brand-neutral-50 border-brand-neutral-10": !active },
      ])}
      onClick={() => {
        if (!disabled) onClick?.();
      }}
    >
      {count && (
        <div className="text-2xl font-semibold leading-[29px]">
          {count || "0"}
        </div>
      )}
      <div className="text-xs whitespace-nowrap leading-4">{label}</div>
    </div>
  );
};

export const PhoneIcon = () => (
  <svg
    width="26"
    height="27"
    viewBox="0 0 26 27"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect y="0.5" width="26" height="26" rx="13" fill="#9F9F9F" />
    <path
      d="M16.9434 19.7846C10.6622 20.0722 5.98467 12.9505 6.63455 9.47575C7.23083 8.45114 7.99512 7.69225 9.01801 7.09229L11.2427 10.1482L10.1145 12.1076C10.1145 12.1076 10.426 13.4157 11.5986 14.5882C12.8292 15.8189 14.2021 16.1952 14.2021 16.1952L16.1615 15.067L19.3269 17.4012C18.738 18.4533 17.9956 19.1958 16.9434 19.7846Z"
      fill="white"
    />
  </svg>
);

export const BlackArrowIcon = () => (
  <svg
    width="16"
    height="17"
    viewBox="0 0 16 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2.92151 5.96094L7.99933 11.0388L13.0771 5.96094"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
  </svg>
);
